import React, { useMemo, useState } from 'react';
import { Modal } from '../../UI/Modal';
import { postService } from '../../../services/postService';
import {
    extractPostIdFromInput,
    mapWorkDetailToExampleWork,
    resolveExternalExampleWorkPreview,
    type ExampleWorkValue
} from './exampleWorkHelpers';

interface ExampleWorkUrlModalProps {
    isOpen: boolean;
    onClose: () => void;
    onImport: (work: ExampleWorkValue) => void;
    zIndex?: number;
}

const ExampleWorkUrlModal: React.FC<ExampleWorkUrlModalProps> = ({
    isOpen,
    onClose,
    onImport,
    zIndex = 80
}) => {
    const [url, setUrl] = useState('');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [showPreview, setShowPreview] = useState(true);
    const [isImporting, setIsImporting] = useState(false);
    const [error, setError] = useState('');

    const canImport = useMemo(() => url.trim().length > 0 && !isImporting, [isImporting, url]);

    const resetState = () => {
        setUrl('');
        setTitle('');
        setDescription('');
        setShowPreview(true);
        setError('');
        setIsImporting(false);
    };

    const handleClose = () => {
        resetState();
        onClose();
    };

    const handleImport = async () => {
        const trimmedUrl = url.trim();
        if (!trimmedUrl) {
            return;
        }

        setIsImporting(true);
        setError('');

        try {
            const postId = extractPostIdFromInput(trimmedUrl);
            if (postId) {
                const detail = await postService.getPostDetail(postId);
                if (!detail) {
                    throw new Error('未找到该本站作品，请检查链接是否正确');
                }

                onImport({
                    ...mapWorkDetailToExampleWork(detail),
                    title: title.trim() || detail.title,
                    description: description.trim() || detail.description || '',
                    sourceUrl: trimmedUrl
                });
                handleClose();
                return;
            }

            try {
                new URL(trimmedUrl);
            } catch {
                throw new Error('请输入有效的 URL');
            }

            const preview = showPreview
                ? await resolveExternalExampleWorkPreview(trimmedUrl)
                : {
                    type: 'image' as const,
                    thumbnailUrl: '',
                    videoUrl: undefined,
                    images: undefined
                };

            onImport({
                id: `url_${Date.now()}`,
                type: preview.type,
                thumbnailUrl: preview.thumbnailUrl,
                videoUrl: preview.videoUrl,
                images: preview.images,
                title: title.trim() || 'URL 参考作品',
                description: description.trim() || '',
                sourceUrl: trimmedUrl
            });
            handleClose();
        } catch (importError: any) {
            setError(importError?.message || '导入链接失败');
        } finally {
            setIsImporting(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="通过 URL 添加参考作品"
            size="md"
            zIndex={zIndex}
            footer={(
                <>
                    <button
                        type="button"
                        onClick={handleClose}
                        className="px-4 py-2 rounded-full text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                    >
                        取消
                    </button>
                    <button
                        type="button"
                        onClick={() => void handleImport()}
                        disabled={!canImport}
                        className={`px-5 py-2 rounded-full text-sm font-medium text-white transition-colors ${canImport ? 'bg-black hover:bg-gray-900' : 'bg-gray-300 cursor-not-allowed'}`}
                    >
                        {isImporting ? '导入中...' : '确认导入'}
                    </button>
                </>
            )}
        >
            <div className="space-y-4">
                <div>
                    <div className="mb-2 flex items-center justify-between gap-4">
                        <label className="block text-sm font-medium text-gray-700">作品链接</label>
                        <button
                            type="button"
                            aria-pressed={showPreview}
                            onClick={() => setShowPreview((prev) => !prev)}
                            className="inline-flex items-center gap-3 rounded-full px-1 py-1 text-sm text-gray-600 transition-colors hover:bg-gray-50"
                        >
                            <span className="text-sm font-medium text-gray-600">显示预览</span>
                            <span className={`inline-flex h-6 w-11 shrink-0 items-center rounded-full p-0.5 transition-colors ${showPreview ? 'justify-end bg-blue-500' : 'justify-start bg-gray-300'}`}>
                                <span className="h-5 w-5 rounded-full bg-white shadow-sm" />
                            </span>
                        </button>
                    </div>
                    <input
                        type="url"
                        value={url}
                        onChange={(event) => setUrl(event.target.value)}
                        placeholder="输入本站作品链接，或任意外站 URL"
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-400"
                    />
                    <p className="mt-2 text-xs text-gray-500">
                        本站作品支持自动识别；外站图片或视频链接会在开启“显示预览”时自动生成预览。
                    </p>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">标题</label>
                    <input
                        type="text"
                        value={title}
                        onChange={(event) => setTitle(event.target.value)}
                        placeholder="可选，不填则使用默认标题"
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-400"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">描述</label>
                    <textarea
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                        placeholder="可选，补充这条参考作品的说明"
                        rows={2}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-400 resize-none"
                    />
                </div>

                {error && (
                    <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
                        {error}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default ExampleWorkUrlModal;
